import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Loader2, Lock, Mail } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { useWebHaptics } from "@/lib/haptics";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls } from "@/lib/seo";
import { emailSchema } from "@/lib/validation";
import { useSession } from "../hooks/auth-hooks";
import { signIn } from "../lib/auth-client";

export const Route = createFileRoute("/login")({
	component: LoginPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/login" });
		const title = "Sign in - AgentMRR";
		const description =
			"Sign in to AgentMRR to manage your AI agent listing, verify revenue with Stripe, and track your MRR on the public leaderboard.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ name: "robots", content: "noindex, follow" },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/login" }),
		};
	},
});

const loginSchema = z.object({
	email: emailSchema,
	password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

function LoginPage() {
	const { trigger } = useWebHaptics();
	const { session, user, isPending, refetch } = useSession();
	const navigate = useNavigate();

	const form = useForm<LoginForm>({
		resolver: zodResolver(loginSchema),
		mode: "onSubmit",
		reValidateMode: "onChange",
		defaultValues: { email: "", password: "" },
	});

	const redirectParam =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search).get("redirect")
			: null;

	const onSubmit = async (values: LoginForm) => {
		try {
			const result = await signIn.email(values);
			if (result.error) {
				const msg = result.error.message ?? "";
				form.setError("root.serverError", {
					message:
						msg.toLowerCase().includes("invalid email") ||
						msg.toLowerCase().includes("invalid credentials")
							? "Incorrect email or password"
							: msg || "Failed to sign in",
				});
				return;
			}
			trigger("success");
			await refetch();
			if (redirectParam) {
				window.location.assign(redirectParam);
			} else {
				navigate({ to: "/dashboard" });
			}
		} catch (err) {
			form.setError("root.serverError", {
				message: err instanceof Error ? err.message : "An error occurred",
			});
		}
	};

	const handleGoogleSignIn = async () => {
		form.clearErrors("root.serverError");
		trigger("success");
		try {
			await signIn.social({
				provider: "google",
				callbackURL: redirectParam || "/dashboard",
			});
		} catch (err) {
			form.setError("root.serverError", {
				message:
					err instanceof Error ? err.message : "Failed to sign in with Google",
			});
		}
	};

	const handleGithubSignIn = async () => {
		form.clearErrors("root.serverError");
		trigger("success");
		try {
			await signIn.social({
				provider: "github",
				callbackURL: redirectParam || "/dashboard",
			});
		} catch (err) {
			form.setError("root.serverError", {
				message:
					err instanceof Error ? err.message : "Failed to sign in with GitHub",
			});
		}
	};

	const shouldRedirectToDashboard = !isPending && !!session && !!user;

	useEffect(() => {
		if (!shouldRedirectToDashboard) return;
		if (redirectParam) {
			window.location.assign(redirectParam);
		} else {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [navigate, redirectParam, shouldRedirectToDashboard]);

	if (shouldRedirectToDashboard) return null;
	if (isPending) return <LoadingScreen />;

	const loading = form.formState.isSubmitting;
	const serverError = form.formState.errors.root?.serverError?.message;

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground pt-[4.5rem]">
			<PublicHeader />
			<main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
				<div className="w-full max-w-[420px]">
					<div className="mb-8">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Auth / login
						</span>
					</div>

					<div className="border border-border bg-card p-6 sm:p-8">
						<div className="space-y-2">
							<h1
								className="font-mono text-2xl leading-tight tracking-[-0.02em]"
								style={{ fontWeight: 400 }}
							>
								<span className="text-brand">/</span>login
							</h1>
							<p className="text-base text-muted-foreground">
								Log in to your AgentMRR dashboard.
							</p>
						</div>

						{serverError && (
							<div className="mt-5 border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-destructive">
								{serverError}
							</div>
						)}

						<div className="mt-6 space-y-3">
							<BrandButton
								type="button"
								variant="outline"
								size="lg"
								onClick={handleGoogleSignIn}
								className="w-full"
							>
								<svg
									className="size-4"
									viewBox="0 0 24 24"
									aria-hidden="true"
									focusable="false"
								>
									<path
										fill="#4285F4"
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									/>
									<path
										fill="#34A853"
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									/>
									<path
										fill="#FBBC05"
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
									/>
									<path
										fill="#EA4335"
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									/>
								</svg>
								Continue with Google
							</BrandButton>

							<BrandButton
								type="button"
								variant="outline"
								size="lg"
								onClick={handleGithubSignIn}
								className="w-full"
							>
								<svg
									className="size-4"
									viewBox="0 0 24 24"
									aria-hidden="true"
									focusable="false"
									fill="currentColor"
								>
									<path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
								</svg>
								Continue with GitHub
							</BrandButton>

							<div className="relative py-1">
								<div className="absolute inset-0 flex items-center">
									<div className="h-px w-full bg-border" />
								</div>
								<div className="relative flex justify-center">
									<span className="bg-card px-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
										Or
									</span>
								</div>
							</div>

							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-4"
									noValidate
								>
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Email</FormLabel>
												<div className="relative">
													<Mail className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
													<FormControl>
														<BrandInput
															autoFocus
															type="email"
															autoComplete="email"
															placeholder="you@domain.com"
															className="pl-9"
															{...field}
														/>
													</FormControl>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex items-center justify-between">
													<FormLabel>Password</FormLabel>
													<Link
														to="/forgot-password"
														className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
													>
														Forgot?
													</Link>
												</div>
												<div className="relative">
													<Lock className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
													<FormControl>
														<BrandInput
															type="password"
															autoComplete="current-password"
															placeholder="••••••••"
															className="pl-9"
															{...field}
														/>
													</FormControl>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>

									<BrandButton
										type="submit"
										size="lg"
										disabled={loading}
										className="mt-1 w-full"
									>
										{loading ? (
											<>
												<Loader2 className="size-3.5 animate-spin" />
												Logging in
											</>
										) : (
											<>
												Log in
												<ArrowRight className="size-3.5" />
											</>
										)}
									</BrandButton>
								</form>
							</Form>
						</div>
					</div>

					<p className="mt-6 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
						No account?{" "}
						<Link
							to="/signup"
							search={redirectParam ? { redirect: redirectParam } : {}}
							className="text-brand transition-colors hover:text-brand-hover"
						>
							Sign up
						</Link>
					</p>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
